import type { InfoShareReplySummary, InfoShareSummary } from '../api/client';
import { buildWebSocketUrl, connectReconnectingSocket, type LiveConnectionState } from './reconnectingSocket';
import type { InfoShareListController } from '../ui/infoShareList';
import type { RepliesDrawerController } from '../ui/infoShareReplies';

interface ShareSnapshotMessage {
  type: 'infoShare.updated' | 'infoShare.created';
  shareId: string;
  displayStatus: string;
  expiresAt: string;
  multiUseAccess: boolean;
  allowGuestReply: boolean;
  unreadReplyCount: number;
  labelBlobId: string | null;
  wasOpened?: boolean;
}

interface ReplyAddedMessage {
  type: 'infoShare.replyAdded';
  shareId: string;
  reply: InfoShareReplySummary;
  unreadReplyCount: number;
}

interface ReplyUpdatedMessage {
  type: 'infoShare.replyUpdated';
  shareId: string;
  replyId: string;
  opened: boolean;
  purgeAt: string | null;
  unreadReplyCount: number;
}

type InfoShareLiveMessage = ShareSnapshotMessage | ReplyAddedMessage | ReplyUpdatedMessage;

let activeSocket: ReturnType<typeof connectReconnectingSocket> | null = null;
let listController: InfoShareListController | null = null;
let repliesController: RepliesDrawerController | null = null;

let onStateChange: ((state: LiveConnectionState) => void) | null = null;
let shareLinkDialogWatcher: ((message: ShareSnapshotMessage) => void) | null = null;

function handleMessage(message: InfoShareLiveMessage): void {
  if (!listController) {
    return;
  }

  switch (message.type) {
    case 'infoShare.created': {
      const existing = listController.getShareState(message.shareId);
      const share: InfoShareSummary = {
        id: message.shareId,
        serverToken: '',
        displayStatus: message.displayStatus,
        createdAt: new Date().toISOString(),
        expiresAt: message.expiresAt,
        multiUseAccess: message.multiUseAccess,
        allowGuestReply: message.allowGuestReply,
        passwordMode: '',
        unreadReplyCount: message.unreadReplyCount,
        labelBlobId: message.labelBlobId ?? existing?.labelBlobId ?? null,
        wasOpened: message.wasOpened ?? existing?.wasOpened ?? false,
      };
      listController.upsertShare(share, 'prepend');
      break;
    }
    case 'infoShare.updated':
      listController.patchFromLiveSnapshot(message);
      shareLinkDialogWatcher?.(message);
      break;
    case 'infoShare.replyAdded':
      listController.patchUnreadCount(message.shareId, message.unreadReplyCount);
      if (repliesController?.shareId === message.shareId) {
        repliesController.addReply(message.reply);
      }
      break;
    case 'infoShare.replyUpdated':
      listController.patchUnreadCount(message.shareId, message.unreadReplyCount);
      if (repliesController?.shareId === message.shareId) {
        repliesController.updateReply(message.replyId, {
          opened: message.opened,
          purgeAt: message.purgeAt,
        });
      }
      break;
    default:
      break;
  }
}

export function connectInfoShareLive(
  controller: InfoShareListController,
  options?: { onStateChange?: (state: LiveConnectionState) => void },
): void {
  disconnectInfoShareLive();
  listController = controller;
  onStateChange = options?.onStateChange ?? null;

  activeSocket = connectReconnectingSocket({
    url: buildWebSocketUrl('/api/ws/info-shares'),
    onMessage: (payload) => handleMessage(payload as InfoShareLiveMessage),
    onStateChange: (state) => onStateChange?.(state),
  });
}

export function registerRepliesDrawer(controller: RepliesDrawerController | null): void {
  repliesController = controller;
}

export function disconnectInfoShareLive(): void {
  activeSocket?.close();
  activeSocket = null;
  listController = null;
  repliesController = null;
  shareLinkDialogWatcher = null;
}

export function registerShareLinkDialogWatcher(
  watcher: ((message: ShareSnapshotMessage) => void) | null,
): void {
  shareLinkDialogWatcher = watcher;
}

export function patchShareFromSummary(share: InfoShareSummary): void {
  listController?.patchShare(share);
}

export function upsertShareFromSummary(share: InfoShareSummary): void {
  listController?.upsertShare(share);
}
